/**
 * Post-op reassessment helpers (AACI charting). Pure, no imports, so
 * tests/unit can load this file with node --test. Mirrors the backend rules
 * in src/services/aaci_service/reassessmentRules.js.
 */

export const APPT_STATUS_CHECK_IN = 5;
export const APPT_STATUS_WAITING_FOR_PAYMENT = 6;
export const CLINICAL_TYPE_IDS = [1, 2];

export function emptyReassessment() {
  return {
    bleeding: null,
    bleedingDetail: "",
    swelling: null,
    swellingDetail: "",
    generalCondition: null,
    generalDetail: "",
    allergicReaction: null,
    allergicDetail: "",
    painScore: null,
    instructionVerbal: false,
    instructionWritten: false,
  };
}

const DETAIL_RULES = [
  ["bleeding", "bleedingDetail", v => v === true],
  ["swelling", "swellingDetail", v => v === true],
  ["generalCondition", "generalDetail", v => v === "DIZZINESS_SYNCOPE"],
  ["allergicReaction", "allergicDetail", v => v === true],
];

const blankText = v => typeof v !== "string" || v.trim() === "";

export function missingReassessmentFields(r) {
  const missing = [];
  ["bleeding", "swelling", "generalCondition", "allergicReaction"].forEach(f => {
    if (r[f] === null || r[f] === undefined) missing.push(f);
  });
  if (!Number.isInteger(r.painScore)) missing.push("painScore");
  if (!r.instructionVerbal && !r.instructionWritten) missing.push("instruction");
  DETAIL_RULES.forEach(([answer, detail, isAbnormal]) => {
    if (isAbnormal(r[answer]) && blankText(r[detail])) missing.push(detail);
  });
  return missing;
}

export function isReassessmentRequired(statusId, items) {
  if (Number(statusId) !== APPT_STATUS_CHECK_IN) return false;
  return (items || []).some(i => CLINICAL_TYPE_IDS.includes(Number(i.typeId)));
}

// The backend accepts a reassessment while the visit is checked in or waiting
// for payment, so the card is offered for both, required or not.
export function canReassess(statusId) {
  return [APPT_STATUS_CHECK_IN, APPT_STATUS_WAITING_FOR_PAYMENT].includes(
    Number(statusId)
  );
}

export function isReassessmentStarted(r) {
  const v = r || {};
  return (
    ["bleeding", "swelling", "generalCondition", "allergicReaction"].some(
      f => v[f] !== null && v[f] !== undefined
    ) ||
    Number.isInteger(v.painScore) ||
    !!v.instructionVerbal ||
    !!v.instructionWritten ||
    DETAIL_RULES.some(([, detail]) => !blankText(v[detail]))
  );
}

export function painBand(score) {
  if (score === 0) return "none";
  if (score <= 3) return "mild";
  if (score <= 6) return "moderate";
  return "severe";
}

export function toReassessmentPayload(r) {
  return {
    bleeding: r.bleeding,
    bleedingDetail: r.bleedingDetail || null,
    swelling: r.swelling,
    swellingDetail: r.swellingDetail || null,
    generalCondition: r.generalCondition,
    generalDetail: r.generalDetail || null,
    allergicReaction: r.allergicReaction,
    allergicDetail: r.allergicDetail || null,
    painScore: r.painScore,
    instructionVerbal: !!r.instructionVerbal,
    instructionWritten: !!r.instructionWritten,
  };
}

export function sameReassessment(current, saved) {
  if (!saved) return false;
  return (
    JSON.stringify(toReassessmentPayload(current)) ===
    JSON.stringify(toReassessmentPayload(saved))
  );
}
